import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import SignalLogo from '@/components/icons/SignalLogo';
import CurveIcon from '@/components/icons/CurveIcon';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { apiService, authService } from '@/services';
import { handleApiError } from '@/utils';
import { useMutation } from '@tanstack/react-query';
import { RoutePath } from '@/routes';
import { mixedCasePasswordSchema, mixedCasePasswordRules } from '@/schemas';
import { cn } from '@/lib/utils';

const acceptInvitationSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  password: mixedCasePasswordSchema,
  password_confirmation: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.password_confirmation, {
  message: 'Passwords do not match',
  path: ['password_confirmation'],
});

type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

type AcceptInvitationPayload = AcceptInvitationFormData & { token: string };

type AcceptInvitationResponse = {
  auth_token: string;
  expires_in: number;
};

// Local on purpose: each auth screen keeps its own copy (see SignUp.tsx).
function RequirementItem({ met, label, testId }: { met: boolean; label: string; testId: string }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId} data-met={met}>
      {met ? (
        <CheckCircle2 className="h-4 w-4 text-trend-positive shrink-0" />
      ) : (
        <XCircle className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <span className={cn('text-xs', met ? 'text-foreground' : 'text-muted-foreground')}>{label}</span>
    </div>
  );
}

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const { mutate: acceptInvitation, isPending } = useMutation<
    AcceptInvitationResponse,
    unknown,
    AcceptInvitationPayload
  >({
    mutationFn: (data) =>
      apiService.post<AcceptInvitationResponse, AcceptInvitationPayload>('/accept-invitation', data),
    onSuccess: (response) => {
      authService.setToken(response.auth_token, response.expires_in);
      navigate(RoutePath.Dashboard, { replace: true });
    },
    onError: (error) => handleApiError(error),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields },
  } = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: { password: '' },
  });

  const passwordValue = watch('password') ?? '';

  const onSubmit = (data: AcceptInvitationFormData) => {
    if (!token) return;
    acceptInvitation({ ...data, token });
  };

  if (!token) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[linear-gradient(to_bottom,#342799,#4C32F2,#A242A2,#FF534C)]">
        <div className="bg-white rounded-[20px] max-w-[550px] px-[50px] py-[52px] w-full text-center">
          <p className="text-gray-800 text-[20px] font-semibold">Invalid invitation link</p>
          <p className="text-gray-500 mt-3 text-sm">This invitation link is missing or has expired.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col space-y-6 items-center bg-[linear-gradient(to_bottom,#342799,#4C32F2,#A242A2,#FF534C)]">
      <div className="pt-[100px]">
        <SignalLogo />
      </div>

      <div className="bg-white rounded-[20px] max-w-[550px] px-[50px] pt-[52px] pb-[58px] w-full">
        <div className="text-gray-800 text-[30px] font-bold">Accept Invitation</div>

        <div className="mt-4 text-[24px] mb-[37px] text-gray-800">
          Set up your account to join your team on Signal.
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-[26px]">
          <div className="grid grid-cols-2 gap-4">
            <div className="w-full space-y-1.5">
              <Label htmlFor="first_name" className="block text-sm text-[#4D4D65] mb-[6px] leading-none">
                First Name
              </Label>
              <Input
                id="first_name"
                type="text"
                placeholder="Jane"
                className={touchedFields.first_name && errors.first_name ? 'ring-2 ring-red-400' : ''}
                {...register('first_name')}
              />
              {touchedFields.first_name && errors.first_name && (
                <p className="leading-none text-red-500 text-sm">{errors.first_name.message}</p>
              )}
            </div>

            <div className="w-full space-y-1.5">
              <Label htmlFor="last_name" className="block text-sm text-[#4D4D65] mb-[6px] leading-none">
                Last Name
              </Label>
              <Input
                id="last_name"
                type="text"
                placeholder="Smith"
                className={touchedFields.last_name && errors.last_name ? 'ring-2 ring-red-400' : ''}
                {...register('last_name')}
              />
              {touchedFields.last_name && errors.last_name && (
                <p className="leading-none text-red-500 text-sm">{errors.last_name.message}</p>
              )}
            </div>
          </div>

          <div className="w-full space-y-1.5">
            <Label htmlFor="password" className="block text-sm text-[#4D4D65] mb-[6px] leading-none">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              className={touchedFields.password && errors.password ? 'ring-2 ring-red-400' : ''}
              {...register('password')}
            />
            {touchedFields.password && errors.password && (
              <p className="leading-none text-red-500 text-sm">{errors.password.message}</p>
            )}
            <div className="space-y-1 pt-1">
              {mixedCasePasswordRules.map((rule) => (
                <RequirementItem
                  key={rule.id}
                  met={rule.test(passwordValue)}
                  label={rule.label}
                  testId={`password-requirement-${rule.id}`}
                />
              ))}
            </div>
          </div>

          <div className="w-full space-y-1.5">
            <Label htmlFor="password_confirmation" className="block text-sm text-[#4D4D65] mb-[6px] leading-none">
              Confirm Password
            </Label>
            <Input
              id="password_confirmation"
              type="password"
              placeholder="Repeat your password"
              className={touchedFields.password_confirmation && errors.password_confirmation ? 'ring-2 ring-red-400' : ''}
              {...register('password_confirmation')}
            />
            {touchedFields.password_confirmation && errors.password_confirmation && (
              <p className="leading-none text-red-500 text-sm">{errors.password_confirmation.message}</p>
            )}
          </div>

          <div className="w-full flex justify-end pt-6">
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Account
            </Button>
          </div>
        </form>
      </div>

      <div className="absolute bottom-2 text-white text-base">@2025 Recom</div>

      <div className="absolute left-[43px] bottom-[50px]">
        <CurveIcon />
      </div>
    </div>
  );
};

export default AcceptInvitation;
